import { LoginRequest, MeResponse } from '../dto/auth.js';
import { HealthV1, PingResponse } from '../dto/health.js';
import { defineRoute } from '../route/define-route.js';
import { z } from '../zod.js';
import { DEMO_CREDENTIALS, EXAMPLE_EVIDENCE, EXAMPLE_HEALTH, EXAMPLE_ME, EXAMPLE_PING, } from './examples.js';
/**
 * Phase 0 routes, frozen at the `design` gate and implemented by P0-2.
 * A change here is a contract change: the route-table snapshot makes it visible in review.
 */
/** Liveness: `ok` to a loopback peer only; 404 to anyone else; 503 when the database stalls. */
export const systemHealthz = defineRoute({
    id: 'system.healthz',
    method: 'GET',
    path: '/healthz',
    response: { kind: 'text' },
    errors: { 503: ['internal'] },
    auth: 'loopback',
    smoke: 'read',
    reference: 'none',
    status: 'frozen',
    owner: 'P0-2',
    since: 'design',
    examples: { response: 'ok' },
});
export const authLogin = defineRoute({
    id: 'auth.login',
    method: 'POST',
    path: '/api/auth/login',
    body: LoginRequest,
    response: { kind: 'empty' },
    errors: { 401: ['invalid_credentials'], 429: ['rate_limited'] },
    auth: 'public',
    smoke: 'deny',
    reference: 'none',
    status: 'frozen',
    owner: 'P0-2',
    since: 'design',
    examples: { request: { body: { ...DEMO_CREDENTIALS } }, response: null },
});
export const authLogout = defineRoute({
    id: 'auth.logout',
    method: 'POST',
    path: '/api/auth/logout',
    response: { kind: 'empty' },
    auth: 'session',
    smoke: 'deny',
    reference: 'none',
    status: 'frozen',
    owner: 'P0-2',
    since: 'design',
    examples: { response: null },
});
export const authMe = defineRoute({
    id: 'auth.me',
    method: 'GET',
    path: '/api/auth/me',
    response: { kind: 'json', schema: MeResponse },
    auth: 'session',
    smoke: 'read',
    reference: 'none',
    status: 'frozen',
    owner: 'P0-2',
    since: 'design',
    examples: { response: EXAMPLE_ME },
});
export const healthGet = defineRoute({
    id: 'health.get',
    method: 'GET',
    path: '/api/health',
    response: { kind: 'json', schema: HealthV1 },
    auth: 'session',
    smoke: 'read',
    reference: 'none',
    status: 'frozen',
    owner: 'P0-2',
    since: 'design',
    examples: { response: EXAMPLE_HEALTH },
});
/** A pg-boss round trip. The one write a smoke session is allowed (`ack-write`). */
export const healthPing = defineRoute({
    id: 'health.ping',
    method: 'POST',
    path: '/api/health/ping',
    response: { kind: 'json', schema: PingResponse },
    errors: { 504: ['worker_timeout'] },
    auth: 'session',
    smoke: 'ack-write',
    reference: 'none',
    status: 'frozen',
    owner: 'P0-2',
    since: 'design',
    examples: { response: EXAMPLE_PING },
});
/**
 * A file inside an evidence bundle. The bundle id is checked by the handler (a bad id or any
 * traversal answers 404), so the params schema only requires strings. `*` is the raw remainder.
 */
export const gateEvidence = defineRoute({
    id: 'gate.evidence',
    method: 'GET',
    path: '/gate/evidence/:id/*',
    params: z.strictObject({ id: z.string().min(1), '*': z.string() }),
    response: { kind: 'file' },
    errors: { 404: ['not_found'] },
    auth: 'session',
    smoke: 'read',
    reference: 'none',
    status: 'frozen',
    owner: 'P0-2',
    since: 'design',
    examples: {
        request: { params: { ...EXAMPLE_EVIDENCE.params } },
        response: { ...EXAMPLE_EVIDENCE.response },
    },
});
//# sourceMappingURL=phase0.js.map